/**
 * LoadFromUrlView.tsx
 *
 * Allows users to load a campaign package by pasting a Frame.io
 * folder URL, bypassing the automatic "to-be-analyzed" folder discovery.
 *
 * URL format: https://next.frame.io/project/{project_id}/{folder_id}
 */

import { useState } from 'react';
import { Link as LinkIcon, ArrowLeft, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { registerCampaignFromUrl, startAnalysis } from '@/api/analysis';

interface RegisteredCampaign {
  campaign_name: string;
  folder_id: string;
  status: string;
  report_id: string;
}

export const LoadFromUrlView = () => {
  const navigate = useNavigate();

  // --- State ---
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [registered, setRegistered] = useState<RegisteredCampaign | null>(null);
  const [registerError, setRegisterError] = useState('');

  const [isStartingAnalysis, setIsStartingAnalysis] = useState(false);
  const [analysisStarted, setAnalysisStarted] = useState(false);

  // --- Handlers ---

  const validateUrl = (value: string): boolean => {
    if (!value.trim()) {
      setUrlError('Please enter a Frame.io folder URL.');
      return false;
    }
    // Must be a next.frame.io URL containing at least one UUID
    const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    if (!value.includes('frame.io') || !uuidPattern.test(value)) {
      setUrlError('Enter a valid Frame.io folder URL, e.g. https://next.frame.io/project/…/…');
      return false;
    }
    setUrlError('');
    return true;
  };

  const handleLoad = async () => {
    if (!validateUrl(url)) return;

    setIsLoading(true);
    setRegisterError('');
    setRegistered(null);

    try {
      const result = await registerCampaignFromUrl(url.trim());
      setRegistered(result);
    } catch (err) {
      setRegisterError(err instanceof Error ? err.message : 'Failed to load campaign from URL.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartAnalysis = async () => {
    if (!registered) return;

    setIsStartingAnalysis(true);
    try {
      await startAnalysis(registered.report_id);
      setAnalysisStarted(true);
    } catch (err) {
      setRegisterError(err instanceof Error ? err.message : 'Failed to start analysis.');
    } finally {
      setIsStartingAnalysis(false);
    }
  };

  const handleReset = () => {
    setUrl('');
    setUrlError('');
    setRegistered(null);
    setRegisterError('');
    setAnalysisStarted(false);
    setIsStartingAnalysis(false);
  };


  return (
    <div className="min-h-screen p-6">
      <div className="max-w-2xl mx-auto">
        {/* Back button */}
        <div className="inline-block bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-6">
          <button
            onClick={() => navigate({ to: '/reports' })}
            className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>
        </div>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <LinkIcon className="w-8 h-8 text-wppBlue" />
            <h1 className="text-4xl font-bold text-gray-900">Load from URL</h1>
          </div>
          <p className="text-gray-600">
            Paste a Frame.io folder URL to load a campaign package directly, without relying on
            the automatic folder discovery.
          </p>
        </div>

        {/* Main card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">

          {/* Step 1 – URL entry (shown until successfully registered) */}
          {!registered && (
            <>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded">
                <p className="text-sm text-blue-800">
                  <strong>How to get the URL:</strong> In Frame.io, open the campaign package
                  folder and copy the URL from your browser address bar.
                </p>
                <p className="text-xs text-blue-700 mt-1 font-mono">
                  https://next.frame.io/project/&#123;project-id&#125;/&#123;folder-id&#125;
                </p>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="frameio-url">
                Frame.io folder URL
              </label>
              <input
                id="frameio-url"
                type="url"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  if (urlError) setUrlError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleLoad();
                }}
                placeholder="https://next.frame.io/project/…/…"
                className={`w-full px-4 py-3 rounded-lg border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-wppBlue transition-colors ${
                  urlError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'
                }`}
                disabled={isLoading}
              />
              {urlError && (
                <p className="mt-2 text-sm text-red-600">{urlError}</p>
              )}

              {registerError && (
                <div className="mt-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{registerError}</p>
                </div>
              )}

              <Button
                onClick={handleLoad}
                disabled={isLoading}
                size="lg"
                className="mt-6 w-full"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Loading campaign…
                  </>
                ) : (
                  <>
                    <LinkIcon className="w-5 h-5 mr-2" />
                    Load Campaign
                  </>
                )}
              </Button>
            </>
          )}

          {/* Step 2 – Campaign loaded, ready to analyse */}
          {registered && !analysisStarted && (
            <>
              <div className="flex items-start gap-4 p-5 bg-green-50 border border-green-200 rounded-lg mb-6">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-800">Campaign loaded</p>
                  <p className="text-sm text-green-700 mt-1">
                    <span className="font-mono">{registered.campaign_name}</span>
                    {' '}is ready to be analysed.
                  </p>
                </div>
              </div>

              <dl className="text-sm text-gray-700 space-y-2 mb-8">
                <div className="flex gap-2">
                  <dt className="font-medium w-32 flex-shrink-0">Campaign name</dt>
                  <dd className="font-mono text-gray-900">{registered.campaign_name}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-medium w-32 flex-shrink-0">Folder ID</dt>
                  <dd className="font-mono text-gray-500 break-all">{registered.folder_id}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="font-medium w-32 flex-shrink-0">Status</dt>
                  <dd>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                      {registered.status}
                    </span>
                  </dd>
                </div>
              </dl>

              {registerError && (
                <div className="mb-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{registerError}</p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={handleStartAnalysis}
                  disabled={isStartingAnalysis}
                  size="lg"
                  className="flex-1"
                >
                  {isStartingAnalysis ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Starting…
                    </>
                  ) : (
                    'Start Analysis'
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={isStartingAnalysis}
                  size="lg"
                >
                  Load different URL
                </Button>
              </div>
            </>
          )}

          {/* Step 3 – Analysis started */}
          {analysisStarted && (
            <div className="text-center py-8">
              <div className="flex items-start gap-4 p-5 bg-green-50 border border-green-200 rounded-lg mb-6">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="font-semibold text-green-800">Analysis started</p>
                  <p className="text-sm text-green-700 mt-1">
                    The campaign is being analyzed. You can check the status on the reports page.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => navigate({ to: '/reports' })}
                  size="lg"
                >
                  View Reports
                </Button>
                <Button
                  variant="outline"
                  onClick={handleReset}
                  size="lg"
                >
                  Load another URL
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
